import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Upload } from "lucide-react";
import { api, formatApiError, formatTL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ymd } from "@/lib/shootOptions";

const sel = "h-9 w-full rounded-lg border border-input bg-white px-2 text-sm";
const ORDER = { AWAITING_PAYMENT: "Ödeme bekleniyor", RECEIPT_UPLOADED: "Dekont incelemede", PAID: "Ödendi · haklar eklendi", CANCELLED: "İptal" };

export function HourPicker({ config, cityId, setCityId, district, setDistrict, day, hour, setHour, exclude }) {
  const [av, setAv] = useState(null);
  const city = config?.cities.find((c) => c.id === cityId);
  useEffect(() => {
    setAv(null);
    if (cityId && day) api.get("/drone/v2/availability", { params: { city_id: cityId, date: ymd(day), exclude } }).then((r) => setAv(r.data)).catch(() => setAv(null));
  }, [cityId, day]);
  return (
    <div className="space-y-3" data-testid="drone-hour-picker">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs font-semibold text-slate-600">Şehir
          <select className={sel} data-testid="drone-city-select" value={cityId || ""} onChange={(e) => { setCityId(e.target.value); setHour(null); }}>
            <option value="">Seçin</option>
            {config?.cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></label>
        {city?.districts?.length > 0 && <label className="text-xs font-semibold text-slate-600">İlçe
          <select className={sel} data-testid="drone-district-select" value={district || ""} onChange={(e) => setDistrict(e.target.value)}>
            <option value="">Seçin</option>{city.districts.map((d) => <option key={d}>{d}</option>)}
          </select></label>}
      </div>
      {av?.closed && <div className="text-xs text-red-600" data-testid="drone-day-closed">Bu gün hizmet verilmiyor veya günlük kapasite dolu.</div>}
      {av && !av.closed && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {av.hours.map((h) => (
            <button key={h.hour} type="button" disabled={!h.available} data-testid={`drone-hour-${h.hour.replace(":", "")}`} onClick={() => setHour(h.hour)}
              className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${!h.available ? "bg-slate-100 text-slate-400 line-through cursor-not-allowed" : hour === h.hour ? "bg-emerald-700 text-white border-emerald-700" : "hover:border-emerald-400"}`}>
              {h.hour}<div className="text-[10px] font-normal">{h.available ? "Boş" : "Dolu"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Balance({ b }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-center" data-testid="drone-credit-balance">
      {[["Aylık ücretsiz", b.free.remaining, "free"], ["Manuel tanımlı", b.MANUAL, "manual"], ["Satın alınan", b.PURCHASED, "purchased"]].map(([l, v, k]) => (
        <div key={k} className="rounded-xl border p-3"><div className="text-2xl font-bold text-emerald-700" data-testid={`credit-${k}`}>{v}</div><div className="text-[11px] text-slate-500">{l}</div></div>
      ))}
    </div>
  );
}

export function DroneCreditsPanel({ config, onChanged }) {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/drone/v2/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const buy = async (p) => {
    if (!window.confirm(`${p.name} · ${formatTL(p.total)} + KDV siparişi oluşturulsun mu?`)) return;
    try { const r = await api.post(`/drone/v2/packages/${p.id}/order`); toast.success(`Sipariş oluşturuldu. Ref: ${r.data.reference_code}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upload = async (o, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/drone/v2/orders/${o.id}/receipt`, fd); toast.success("Dekont yüklendi"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!config?.balance) return null;
  return (
    <section className="space-y-3" data-testid="drone-credits-panel">
      <h2 className="text-base md:text-lg font-semibold text-slate-900 flex items-center gap-2"><Package className="w-5 h-5 text-emerald-700" />Çekim haklarım ve ek paketler</h2>
      <Balance b={config.balance} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {config.packages.map((p) => (
          <Card key={p.id} className="p-4 space-y-1" data-testid={`drone-package-${p.id}`}>
            <div className="font-semibold">{p.name}</div>
            <div className="text-xs text-slate-500">{p.description}</div>
            <div className="text-sm">{p.shots} çekim × {formatTL(p.unit_price)}{p.discount_pct > 0 && <span className="text-emerald-700"> · %{p.discount_pct} indirim</span>}</div>
            <div className="text-lg font-bold">{formatTL(p.total)} <span className="text-xs font-normal text-slate-500">+ KDV · {p.validity_days} gün geçerli</span></div>
            <Button size="sm" className="w-full" data-testid={`drone-package-buy-${p.id}`} onClick={() => buy(p)}>Satın al (Havale/EFT)</Button>
          </Card>
        ))}
        {config.packages.length === 0 && <Card className="p-4 text-sm text-slate-400">Şu an satışta ek paket yok</Card>}
      </div>
      {orders.length > 0 && <Card className="divide-y">
        {orders.map((o) => (
          <div key={o.id} className="p-3 flex flex-wrap items-center gap-2 text-sm" data-testid={`drone-order-${o.id}`}>
            <span className="font-mono font-semibold">{o.reference_code}</span><span>{o.package.name}</span><span className="font-semibold">{formatTL(o.total)}</span>
            <Badge variant="outline">{ORDER[o.status]}</Badge>
            {o.status === "AWAITING_PAYMENT" && <label className="ml-auto text-xs font-semibold text-emerald-700 cursor-pointer inline-flex items-center gap-1"><Upload className="w-3.5 h-3.5" />Dekont yükle
              <input type="file" className="hidden" accept="application/pdf,image/*" data-testid={`drone-order-receipt-${o.id}`} onChange={(e) => e.target.files?.[0] && upload(o, e.target.files[0])} /></label>}
          </div>
        ))}
      </Card>}
    </section>
  );
}
